import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileUp, Loader2, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";

type ImportStatus = "inserted" | "updated" | "skipped" | "error";

interface ImportResult {
  filename: string;
  status: ImportStatus;
  slug?: string;
  id?: number;
  title?: string;
  message?: string;
}

interface ImportResponse {
  results: ImportResult[];
  summary: Record<ImportStatus, number>;
}

interface AdminBlogPost {
  id: number;
  title: string;
  slug: string | null;
  featured: boolean;
  category: string | null;
  author: string | null;
  corePage: string | null;
  source: string | null;
  automationTopic: string | null;
  publishedAt: string | null;
  updatedAt: string | null;
  createdAt: string;
}

interface BlogSettings {
  flags: { blogAutomationEnabled: boolean; outrankWebhookEnabled: boolean };
  topics: { total: number; used: number; remaining: number; invalid: number; next: { topic: string } | null; error?: string };
}

const POSTS_KEY = ["/api/admin/blog/posts"];
const SETTINGS_KEY = ["/api/admin/blog/settings"];
const MAX_FILES = 50;
const MAX_TOTAL_BYTES = 4 * 1024 * 1024;

const STATUS_VARIANT: Record<ImportStatus, "default" | "secondary" | "outline" | "destructive"> = {
  inserted: "default",
  updated: "secondary",
  skipped: "outline",
  error: "destructive",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

/** Admin → Blog: Markdown import, recent posts with featured pin, automation status. */
export function BlogImportPanel() {
  return (
    <div className="space-y-6" data-testid="panel-admin-blog">
      <div>
        <h3 className="text-lg font-semibold">Blog</h3>
        <p className="text-sm text-muted-foreground">
          Import Markdown posts, pin featured posts, and check the daily automation. Changes are written to the admin audit log.
        </p>
      </div>
      <ImportCard />
      <AutomationCard />
      <RecentPostsCard />
    </div>
  );
}

function ImportCard() {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [results, setResults] = useState<ImportResult[] | null>(null);

  const importMutation = useMutation({
    mutationFn: async (selected: File[]) => {
      const payload = await Promise.all(selected.map(async (f) => ({ filename: f.name, content: await f.text() })));
      const res = await apiRequest("POST", "/api/admin/blog/import", { files: payload });
      return (await res.json()) as ImportResponse;
    },
    onSuccess: (data) => {
      setResults(data.results);
      const { inserted, updated, skipped, error } = data.summary;
      toast({
        title: "Import finished",
        description: `${inserted} inserted, ${updated} updated, ${skipped} skipped, ${error} error(s).`,
        variant: error > 0 ? "destructive" : "default",
      });
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      void queryClient.invalidateQueries({ queryKey: POSTS_KEY });
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
    onError: (err) => {
      toast({ title: "Couldn't import the files", description: parseErrorMessage(err, "Please check the files and try again."), variant: "destructive" });
    },
  });

  const totalBytes = files.reduce((n, f) => n + f.size, 0);
  const tooMany = files.length > MAX_FILES;
  const tooBig = totalBytes > MAX_TOTAL_BYTES;

  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-blog-import">
      <div className="flex items-center gap-2">
        <FileUp className="h-4 w-4 text-brand" />
        <h4 className="font-semibold">Import Markdown</h4>
      </div>
      <p className="text-sm text-muted-foreground">
        Front matter keys: <code>title</code>, <code>slug</code>, <code>description</code>, <code>date</code>,{" "}
        <code>featured</code>, <code>category</code>, <code>author</code>, and optional <code>corePage</code> (messages,
        maintenance, locks, leasing or numbers). Posts are matched by slug: an existing slug is updated. A future date
        schedules the post; it appears on that day.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="blog-import-files">Markdown files</Label>
        <input
          id="blog-import-files"
          ref={inputRef}
          type="file"
          accept=".md,.markdown,text/markdown"
          multiple
          className="block w-full text-sm"
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          data-testid="input-blog-import-files"
        />
        {files.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="text-blog-import-selection">
            {files.length} file(s) selected{tooMany ? ` — choose ${MAX_FILES} or fewer` : ""}
            {tooBig ? " — total size is over 4 MB, import in smaller batches" : ""}
          </p>
        )}
      </div>
      <Button
        onClick={() => importMutation.mutate(files)}
        disabled={files.length === 0 || tooMany || tooBig || importMutation.isPending}
        data-testid="button-blog-import"
      >
        {importMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
        Import {files.length > 0 ? `${files.length} file(s)` : ""}
      </Button>

      {results && (
        <Table data-testid="table-blog-import-results">
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((r, i) => (
              <TableRow key={`${r.filename}-${i}`} data-testid={`row-blog-import-${i}`}>
                <TableCell className="font-mono text-xs">{r.filename}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {r.slug && r.status !== "error" ? (
                    <a href={`/blog/${r.slug}`} target="_blank" rel="noopener noreferrer" className="text-brand underline">
                      {r.slug}
                    </a>
                  ) : (
                    r.slug ?? "—"
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.message ?? ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function AutomationCard() {
  const { toast } = useToast();
  const settingsQuery = useQuery<BlogSettings>({ queryKey: SETTINGS_KEY });

  const flagMutation = useMutation({
    mutationFn: async (vars: { key: keyof BlogSettings["flags"]; enabled: boolean }) => {
      const res = await apiRequest("POST", "/api/admin/blog/settings", vars);
      return (await res.json()) as { key: string; enabled: boolean };
    },
    onSuccess: (data) => {
      toast({ title: "Setting saved", description: `${data.key} is now ${data.enabled ? "on" : "off"}.` });
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
    onError: (err) => {
      toast({ title: "Couldn't save the setting", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const data = settingsQuery.data;
  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-blog-automation">
      <h4 className="font-semibold">Daily automation</h4>
      <p className="text-sm text-muted-foreground">
        Travis flips these. The automation writes at most one post a day from <code>content/blog-topics.json</code>, and
        only while it is on and unused topics remain. The Outrank webhook is retired; keep it off.
      </p>
      {settingsQuery.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : settingsQuery.isError || !data ? (
        <p className="text-sm text-destructive">{parseErrorMessage(settingsQuery.error, "Couldn't load the blog settings.")}</p>
      ) : (
        <div className="space-y-3">
          {(
            [
              ["blogAutomationEnabled", "Daily blog automation"],
              ["outrankWebhookEnabled", "Outrank webhook"],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <Label htmlFor={`blog-flag-${key}`}>{label}</Label>
              <Switch
                id={`blog-flag-${key}`}
                checked={data.flags[key]}
                disabled={flagMutation.isPending}
                onCheckedChange={(enabled) => flagMutation.mutate({ key, enabled })}
                data-testid={`switch-blog-flag-${key}`}
              />
            </div>
          ))}
          <p className="text-sm" data-testid="text-blog-topics-status">
            Topics: {data.topics.total} total, {data.topics.used} used, {data.topics.remaining} remaining
            {data.topics.invalid > 0 ? `, ${data.topics.invalid} invalid (skipped)` : ""}.
            {data.topics.next ? ` Next: “${data.topics.next.topic}”.` : ""}
            {data.topics.error ? ` ${data.topics.error}.` : ""}
          </p>
        </div>
      )}
    </Card>
  );
}

function RecentPostsCard() {
  const { toast } = useToast();
  const postsQuery = useQuery<AdminBlogPost[]>({ queryKey: POSTS_KEY });

  const featuredMutation = useMutation({
    mutationFn: async (vars: { id: number; featured: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/blog/posts/${vars.id}/featured`, { featured: vars.featured });
      return (await res.json()) as { id: number; featured: boolean };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POSTS_KEY });
    },
    onError: (err) => {
      toast({ title: "Couldn't update the post", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const now = Date.now();
  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-blog-posts">
      <h4 className="font-semibold">Recent posts</h4>
      {postsQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : postsQuery.isError ? (
        <p className="text-sm text-destructive">{parseErrorMessage(postsQuery.error, "Couldn't load the posts.")}</p>
      ) : (postsQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No posts yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="table-blog-posts">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Core page</TableHead>
                <TableHead className="text-right">Featured</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(postsQuery.data ?? []).map((p) => {
                const scheduled = p.publishedAt ? new Date(p.publishedAt).getTime() > now : false;
                return (
                  <TableRow key={p.id} data-testid={`row-blog-post-${p.id}`}>
                    <TableCell>
                      <div className="font-medium">{p.title}</div>
                      <div className="font-mono text-xs text-muted-foreground">{p.slug ?? "(no slug)"}</div>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDate(p.publishedAt ?? p.createdAt)}
                      {scheduled && (
                        <Badge variant="outline" className="ml-2">
                          scheduled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{p.source ?? "—"}</TableCell>
                    <TableCell className="text-sm">{p.corePage ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant={p.featured ? "default" : "outline"}
                        size="sm"
                        disabled={featuredMutation.isPending}
                        onClick={() => featuredMutation.mutate({ id: p.id, featured: !p.featured })}
                        aria-pressed={p.featured}
                        data-testid={`button-blog-featured-${p.id}`}
                      >
                        <Star className="mr-1 h-3.5 w-3.5" />
                        {p.featured ? "Pinned" : "Pin"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
